/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type React from "react"
import { useState, useRef, useCallback, useEffect } from "react"
import { IoClose } from "react-icons/io5"
import NaverMapView from "@/components/views/NaverMapView"
import type { PlaceData } from "./PlaceSelector"
import "./PlaceSearchModal.css"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import {
  updateGPSByUser,
  selectGPS,
} from "@/features/maps/mapsSlice"

type PlaceSearchModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (place: PlaceData) => void
  mode?: "SELECT_EXISTING" | "SELECT_COORDINATE"
  initialLocation?: { lat: number; lng: number }
}

export const PlaceSearchModal: React.FC<PlaceSearchModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  mode = "SELECT_COORDINATE",
  initialLocation,
}) => {
  const dispatch = useAppDispatch()
  const currentGPS = useAppSelector(selectGPS)

  const [mapInstance, setMapInstance] = useState<any>(null)
  const [selectedPlace, setSelectedPlace] = useState<PlaceData | null>(null)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const isGeocodingRef = useRef(false)
  
  // 모드에 따른 안내 문구
  const modeInstruction = mode === "SELECT_EXISTING" 
    ? "지도에 표시된 캠핑장을 선택해주세요" 
    : "지도를 움직여 위치를 선택해주세요"

  // Removed fetchCampingSites since it's no longer managed by Modal

  // Center logic
  // If initialLocation provided, use it. Otherwise use currentGPS defaults
  const centerToUse = initialLocation || currentGPS

  const reverseGeocode = useCallback((coords: any) => {
    const naver = (window as any).naver
    if (!naver?.maps?.Service) return

    isGeocodingRef.current = true
    setIsGeocoding(true)
    
    naver.maps.Service.reverseGeocode(
      {
        coords: coords,
        orders: [
          naver.maps.Service.OrderType.ADDR,
          naver.maps.Service.OrderType.ROAD_ADDR,
        ].join(","),
      },
      (status: any, response: any) => {
        if (status === naver.maps.Service.Status.OK) {
          const result = response.v2
          const address =
            result.address.roadAddress ?? result.address.jibunAddress
          
          setSelectedPlace({
            name: "선택된 위치",
            address: address,
            lat: coords.y,
            lng: coords.x,
          })
        }
        setIsGeocoding(false)
        isGeocodingRef.current = false
      },
    )
  }, [])

  useEffect(() => {
    if (!mapInstance || !isOpen) return

    const naver = (window as any).naver
    
    // Only bind geocoding on drag if in COORDINATE mode
    if (mode === "SELECT_COORDINATE") {
        const listener = naver.maps.Event.addListener(
          mapInstance,
          "dragend",
          () => {
            if (!isGeocodingRef.current) {
              reverseGeocode(mapInstance.getCenter())
            }
          },
        )
        
        // Also trigger once on idle (initial load) if no initial place selected
        const idleListener = naver.maps.Event.once(mapInstance, "idle", () => {
             reverseGeocode(mapInstance.getCenter())
        })
    
        return () => {
          naver.maps.Event.removeListener(listener)
          naver.maps.Event.removeListener(idleListener)
        }
    }
  }, [mapInstance, isOpen, reverseGeocode, mode])


  const handleCurrentLocationClick = () => {
     void dispatch(updateGPSByUser())
     // updateGPSByUser will update the gps state, which NaverMapView listens to and auto-centers
  }

  if (!isOpen) return null

  return (
    <div className="place-search-modal-overlay">
      <div className="place-search-modal-content">
        <header className="modal-header">
          <h3>장소 선택</h3>
          <button className="close-btn" onClick={onClose} type="button">
            <IoClose />
          </button>
        </header>
        
        <div className="modal-map-container">
          <NaverMapView
            initialCenter={centerToUse}
            initialZoom={14}
            onMapLoad={setMapInstance}
          />
          {mode === "SELECT_COORDINATE" && <div className="center-marker"></div>}
           <button 
                className="current-location-btn"
                onClick={handleCurrentLocationClick}
                type="button"
            >
                현재 위치
            </button>
        </div>
        
        <div className="modal-footer">
          <div className="selected-address-info">
             {selectedPlace ? (
                 <>
                    <div className="place-name-text">{selectedPlace.name}</div>
                    <div className="address-text">{selectedPlace.address}</div>
                 </>
             ) : (
                 <div className="address-text">{modeInstruction}</div>
             )}
          </div>
          <button 
            className="confirm-btn" 
            disabled={!selectedPlace || isGeocoding}
            onClick={() => { if(selectedPlace) onConfirm(selectedPlace); }}
            type="button"
          >
            {isGeocoding ? "주소 확인 중..." : "이 위치로 설정"}
          </button>
        </div>
      </div>
    </div>
  )
}
